import { FunctionsHttpError } from "@excel-center/supabase";
import type { InvitationRow, ProfileRow } from "@/src/auth/types/auth.types";
import { supabase } from "@/src/common/services/supabase.service";

export type AcceptInvitationResult = "ok" | "already_registered" | "invitation_invalid";

export interface ProfileWithSchools {
	profile: ProfileRow;
	schoolIds: string[];
}

/**
 * How a given email should authenticate:
 * - `password` — has a password set; show the password field
 * - `otp` — allowed but no password yet; first-time claim sets one directly
 * - `unregistered` — not allowed to sign in
 */
export type AuthMethod = "password" | "otp" | "unregistered";

/**
 * Thin, typed wrapper around `supabase.auth.*` and auth-related table
 * reads. Keep all direct Supabase SDK calls related to authentication
 * inside this file; UI talks to the slice/hooks.
 */
export const authService = {
	async getAuthMethod(email: string): Promise<AuthMethod> {
		const { data, error } = await supabase.rpc("auth_method_for_email", {
			p_email: email.trim().toLowerCase(),
		});
		if (error) throw new Error(error.message);
		return data === "password" || data === "otp" ? data : "unregistered";
	},

	async signInWithPassword(email: string, password: string): Promise<void> {
		const { error } = await supabase.auth.signInWithPassword({
			email: email.trim().toLowerCase(),
			password,
		});
		if (error) throw new Error(error.message);
	},

	async setPassword(password: string): Promise<void> {
		const { error } = await supabase.auth.updateUser({
			password,
			data: { password_set: true },
		});
		if (error) throw new Error(error.message);
	},

	// Password-reset only: the account always exists by the time a code is sent.
	async requestOtp(email: string): Promise<void> {
		const { error } = await supabase.auth.signInWithOtp({
			email: email.trim().toLowerCase(),
			options: { shouldCreateUser: false },
		});
		if (error) throw new Error(error.message);
	},

	async verifyOtp(email: string, token: string): Promise<void> {
		const { error } = await supabase.auth.verifyOtp({
			email: email.trim().toLowerCase(),
			token,
			type: "email",
		});
		if (error) throw new Error(error.message);
	},

	async claimAccount(email: string, password: string): Promise<void> {
		const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>("claim-account", {
			body: { email: email.trim().toLowerCase(), password },
		});
		if (error) {
			if (error instanceof FunctionsHttpError) {
				const body: unknown = await error.context.json().catch(() => null);
				const code = body && typeof body === "object" ? (body as { error?: unknown }).error : null;
				if (typeof code === "string") throw new Error(code);
			}
			throw new Error(error.message);
		}
		if (!data?.ok) throw new Error(data?.error ?? "Account claim failed.");
	},

	async acceptInvitation(token: string, password: string): Promise<AcceptInvitationResult> {
		const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>("accept-invitation", {
			body: { token, password },
		});
		if (error) {
			if (error instanceof FunctionsHttpError) {
				const body: unknown = await error.context.json().catch(() => null);
				const code = body && typeof body === "object" ? (body as { error?: unknown }).error : null;
				if (code === "already_registered" || code === "invitation_invalid") return code;
				if (typeof code === "string") throw new Error(code);
			}
			throw new Error(error.message);
		}
		if (!data?.ok) throw new Error(data?.error ?? "Invitation acceptance failed.");
		return "ok";
	},

	async signOut(): Promise<void> {
		const { error } = await supabase.auth.signOut();
		if (error) throw new Error(error.message);
	},

	async getSession() {
		const { data, error } = await supabase.auth.getSession();
		if (error) throw new Error(error.message);
		return data.session;
	},

	async fetchInvitationByToken(token: string): Promise<InvitationRow | null> {
		const { data, error } = await supabase
			.from("invitations")
			.select("*")
			.eq("token", token)
			.is("accepted_at", null)
			.is("revoked_at", null)
			.gt("expires_at", new Date().toISOString())
			.maybeSingle();
		if (error) throw new Error(error.message);
		return data ?? null;
	},

	async fetchProfileWithSchools(userId: string): Promise<ProfileWithSchools | null> {
		const [profileRes, staffSchoolsRes] = await Promise.all([
			supabase.from("profiles").select("*").eq("id", userId).maybeSingle(),
			supabase.from("staff_schools").select("school_id").eq("staff_id", userId),
		]);
		if (profileRes.error) throw new Error(profileRes.error.message);
		if (staffSchoolsRes.error) throw new Error(staffSchoolsRes.error.message);
		const profile = profileRes.data;
		if (!profile) return null;
		const schoolIds = (staffSchoolsRes.data ?? []).map((row) => row.school_id);
		return { profile, schoolIds };
	},
};
